/**
 * The letter that hands the customer their copy of what they signed.
 *
 * Every other business that takes an electronic signature sends one of these,
 * and until 2026-09-09 we did not: signing wrote the acceptance, told the rep,
 * and — only where money was due that day — asked how they would like to pay.
 * A customer who had just agreed to spend eighteen thousand dollars was left
 * with a browser tab and nothing to keep. Katie Harding said so.
 *
 * It is deliberately **not** about money. The payment letter in
 * `paymentEmails.ts` asks for the first payment and chases it; this one is the
 * record, it goes out whether or not a payment is due, and a customer who owes
 * nothing today still gets it. Two letters saying different things beats one
 * that only some customers receive.
 *
 * The link is the same `/p/<token>` page they signed on, which after signing
 * shows the signed document — theirs to read, print or save — rather than the
 * document all over again.
 *
 * Plain text, like the payment letters, for the same reason: `buildRawMessage`
 * makes the HTML part from it, and plain text survives every mail client a
 * homeowner might be using.
 *
 * Transcribed for Deno in `supabase/functions/_shared/signed-copy.ts`, and the
 * two are pinned together by `supabase/test/signed-copy-match.test.ts`.
 * **Change one, change both.**
 */

import {
  emailRefusal,
  looksLikeEmailAddress,
  type EmailableContact,
  type EmailRefusalReason,
} from "./gmail.js";
import { formatMoney, type Cents } from "./money.js";
import {
  chosenPackages,
  type Chosen,
  type PresentedDocument,
} from "./presentation.js";
import { recordUpgrades } from "./signedDocument.js";

/**
 * The letter's money is written as a customer in the US reads it, whatever the
 * machine sending it is set to. The Edge Function's copy is pinned the same
 * way, so the two cannot print one figure two ways.
 */
const LETTER_LOCALE = "en-US";
const money = (value: Cents): string => formatMoney(value, LETTER_LOCALE);

/** One line of what they bought: a package, or an upgrade they ticked. */
export interface SignedCopyLine {
  name: string;
  /** Null where the package price already covered it — printed "Included". */
  amount: Cents | null;
}

export interface SignedCopyLetterInput {
  /** What the customer is called. First name where we have one. */
  customerName?: string | null;
  /** Where the work is, so the letter is obviously about their roof. */
  address?: string | null;
  /**
   * The date the customer wrote beside their name, as `yyyy-mm-dd`.
   *
   * Theirs, not the server's clock: it is the date on the contract and the one
   * the three days to cancel run from, so it is the one the copy repeats back.
   */
  signedDate?: string | null;
  /** What they bought, in the order the document listed it. */
  lines: readonly SignedCopyLine[];
  /** The receipt total, as the acceptance recorded it. */
  total: Cents;
  /** The `/p/<token>` page, which now opens their signed copy. */
  link: string;
  /** Who it is from. Their full name (never "Sam"). */
  repName?: string | null;
  /** The rep's own mobile — the letter is signed by a person. */
  repPhone?: string | null;
  /** The trading name, out of `company_settings` (CLAUDE.md rule 11). */
  companyName: string;
  /** The number to call when the rep has none on file. */
  companyPhone?: string | null;
}

export interface SignedCopyLetter {
  subject: string;
  text: string;
}

/**
 * "Hi Katie," where we know, "Hello," where we do not.
 *
 * First name only, the same rule the payment letters follow: a letter opening
 * "Hi Katie Harding," reads like a bill from somebody who has never met her.
 */
function greeting(name?: string | null): string {
  const first = (name ?? "").trim().split(/\s+/)[0];
  return first ? `Hi ${first},` : "Hello,";
}

/** " at 12 Oak Street" — omitted entirely when we have no address. */
function atTheAddress(address?: string | null): string {
  const trimmed = (address ?? "").trim();
  return trimmed ? ` at ${trimmed}` : "";
}

/**
 * "September 8, 2026", from the `yyyy-mm-dd` the customer typed, or null when
 * it is not one.
 *
 * Split by hand rather than parsed: `new Date("2026-09-08")` is midnight UTC
 * and prints as the seventh to anybody west of Greenwich, which is everybody
 * reading this. The same trap that made "Signed this month" read zero (#171).
 *
 * Exported because the letter and the page its link opens both say when the
 * customer signed, and they should say it the same way.
 */
export function signedDateWords(date?: string | null): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((date ?? "").trim());
  if (!match) return null;
  const [, year, month, day] = match;
  const name = MONTHS[Number(month) - 1];
  if (!name) return null;
  return `${name} ${Number(day)}, ${year}`;
}

/** " on September 8, 2026" — omitted entirely when we have no date. */
function onTheDate(date?: string | null): string {
  const words = signedDateWords(date);
  return words ? ` on ${words}` : "";
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * What they bought, as lines somebody can check against the document.
 *
 * Two spaces of indent rather than a table: this is plain text on a phone, and
 * anything cleverer wraps into nonsense at 320 pixels.
 */
function whatTheyBought(lines: readonly SignedCopyLine[]): string[] {
  if (lines.length === 0) return [];
  return [
    "What you signed for:",
    "",
    ...lines.map(
      (line) =>
        `  ${line.name} — ${line.amount === null ? "Included" : money(line.amount)}`,
    ),
  ];
}

function signOff(input: SignedCopyLetterInput): string {
  const name = (input.repName ?? "").trim();
  // The rep's own number first, for the same reason the payment letters do it:
  // the letter comes from their mailbox and is signed with their name.
  const phone =
    (input.repPhone ?? "").trim() || (input.companyPhone ?? "").trim();
  const call = phone
    ? `reply to this email or call me on ${phone}`
    : "reply to this email";
  return [
    `If anything on it does not look right, ${call}.`,
    "",
    "Thank you,",
    ...(name ? [name] : []),
    input.companyName,
  ].join("\n");
}

/**
 * The copy, sent the moment the signature lands.
 *
 * The total is the one on the acceptance — the receipt written at the moment
 * of signing — not a figure worked out again later, so this letter and the
 * document behind the link cannot disagree about what was agreed.
 */
export function signedCopyLetter(
  input: SignedCopyLetterInput,
): SignedCopyLetter {
  return {
    subject: `Your signed agreement with ${input.companyName}`,
    text: [
      greeting(input.customerName),
      "",
      `Thank you for choosing ${input.companyName}. This is your copy of the ` +
        `agreement you signed${onTheDate(input.signedDate)} for the work` +
        `${atTheAddress(input.address)}.`,
      "",
      ...whatTheyBought(input.lines),
      "",
      `Total: ${money(input.total)}`,
      "",
      "You can read the whole agreement, and print or save it, here:",
      "",
      input.link,
      "",
      // The link is the only copy they have, so the letter asks them to keep
      // it. It does not promise the link lasts for ever: voiding a signed
      // proposal closes it, and a letter saying otherwise would be wrong the
      // day that happens. What it promises instead is a person.
      "Keep this email so you can come back to it. If the link ever stops " +
        "working, let us know and we will sort it out.",
      "",
      signOff(input),
    ].join("\n"),
  };
}

/**
 * What they bought, as lines for their copy, off the frozen document.
 *
 * The package they chose at the price it was offered at, then every upgrade
 * that ended up on the roof — a ticked one at its price, one the package
 * already covered at `null`, which the letter prints as "Included". The same
 * two lookups the office's record makes (`chosenPackage`, `recordUpgrades`), so
 * the letter and the document behind its link list the same things.
 *
 * Until 2026-09-10 there were two of these: the Edge Function listed only the
 * upgrades the customer ticked, and the Docs button listed the included ones
 * too, so the same contract produced two different letters depending on who
 * sent it. This is the one; `_shared/signed-copy.ts` transcribes it and
 * `supabase/test/signed-copy-match.test.ts` holds the two together.
 *
 * Every package they bought, in the document's order, where the document let
 * them buy more than one (ADR 0120): a letter naming the roof and leaving out
 * the gutters beside it describes a contract nobody signed. An upgrade is
 * included when any of them includes it, the same rule the page and the
 * signature follow.
 */
export function signedCopyLines(
  document: PresentedDocument,
  chosen: Chosen,
  upgradeKeys: readonly string[],
): SignedCopyLine[] {
  return [
    ...chosenPackages(document, chosen).map((pack) => ({
      name: pack.name,
      amount: pack.total,
    })),
    ...recordUpgrades(document, chosen, upgradeKeys).map(
      ({ upgrade, included }) => ({
        name: upgrade.name,
        amount: included ? null : upgrade.price,
      }),
    ),
  ];
}

/**
 * Why a copy did not go, said to whoever reads the row in Docs.
 *
 * The same four reasons `emailRefusal` knows, worded for this letter: the
 * reader is looking at a signed contract and wants to know what to do about
 * the copy, not about email in general.
 */
export const SIGNED_COPY_REFUSALS: Record<EmailRefusalReason, string> = {
  blacklisted:
    "This customer is blacklisted, so their copy was not sent. Clear the " +
    "flag on their contact if that is wrong.",
  no_automated_contact:
    "This customer is marked no automated contact, so their copy was not " +
    "sent. A person can still call.",
  no_address:
    "There is no email address for this customer that the app can use, so " +
    "their copy could not be sent. Add one to their contact and try again.",
  bad_address:
    "The email address on this customer's contact does not look right, so " +
    "their copy could not be sent. Fix it and try again.",
};

export type SignedCopyRecipient =
  | { ok: true; to: string }
  | { ok: false; reason: EmailRefusalReason; error: string };

/**
 * Who their copy goes to, or why it goes to nobody.
 *
 * The address on the signature first — the form asks for one, starts it
 * filled in from the contact, and where they differ the one on the contract
 * reaches the person who signed it. But that box is free text typed by
 * whoever holds the link, so it is used only when it is exactly one address:
 * a comma or a semicolon there would otherwise have the rep's own Gmail send
 * the contract to every address somebody cared to type (a mail header carries
 * a list). Anything else falls back to the contact's own address, which the
 * office entered.
 *
 * The contact's flags stop it either way. `blacklisted` and
 * `no_automated_contact` belong to the person, not to an address, and a letter
 * the app composes and sends is automated contact however it was triggered —
 * the same rule `emailRefusal` applies to every other send.
 */
export function signedCopyRecipient(
  signerEmail: string | null | undefined,
  contact: Partial<EmailableContact> | null | undefined,
): SignedCopyRecipient {
  const refused = (reason: EmailRefusalReason): SignedCopyRecipient => ({
    ok: false,
    reason,
    error: SIGNED_COPY_REFUSALS[reason],
  });

  if (contact?.blacklisted) return refused("blacklisted");
  if (contact?.no_automated_contact) return refused("no_automated_contact");

  const signer = (signerEmail ?? "").trim();
  if (signer !== "" && looksLikeEmailAddress(signer)) {
    return { ok: true, to: signer };
  }

  const fallback = (contact?.email ?? "").trim();
  const refusal = emailRefusal({ email: fallback }, fallback);
  return refusal ? refused(refusal.reason) : { ok: true, to: fallback };
}
